import { useState, useEffect, useCallback } from "react";

import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

import SpinnerCentered from "~/components/Loader/SpinnerCentered";
import ButtonPrimary from "~/components/Buttons/ButtonPrimary";
import { getUsers } from "services/http/users";
import { httpGetInvitedUsers } from "services/http/companies";

const useStyles = makeStyles((theme) => ({
  buttonLink: {
    textDecoration: "none",
  },
  overrides: {
    MuiTableCell: {
      root: {
        fontWeight: "bold",
        backgroundColor: "lightblue",
        "&:last-child": {
          paddingRight: 5,
        },
      },
    },
  },
  content: {
    padding: 48,
    overflowX: "auto",
  },
  title: {
    paddingBottom: 32,
  },
  table: {
    minWidth: 650,
    borderWidth: 1,
    borderColor: theme.palette.table.border,
    borderStyle: "solid",
    fontSize: 16,
    fontWeight: "bold",
  },
  tableHeaderCell: {
    color: theme.palette.table.thead.text,
    fontSize: 16,
    fontWeight: "bold",
    backgroundColor: theme.palette.table.thead.background,
  },
  logo: {
    width: 24,
    height: 24,
    marginLeft: 2,
  },
  notConfirmed: {
    color: "#B5B5B5",
  },
  confirmed: {
    color: "#87CC72",
  },
}));

const UsersList = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { myCompany, myUser } = props;

  const [rows, setRows] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleGetUsers = useCallback(() => {
    setLoading(true);
    const request = {
      params: {
        admins: true,
        drivers: true,
        company: myCompany?.id,
      },
    };

    getUsers(request).then(
      (response) => {
        let _users = [];
        response.map((user) => {
          if (user.status == 100) {
            _users.push(user);
          }
        });
        setRows(_users);
        setLoading(false);
      },
      (_error) => {
        setLoading(false);
      },
    );
  }, [myCompany?.id]);

  const handleGetUserInvites = useCallback(() => {
    setLoading(true);
    const request = {};

    httpGetInvitedUsers(request).then(
      (response) => {
        let _users = [];
        response.map((user) => {
          if (user?.status == 100) {
            _users.push(user);
          }
        });
        setInvitedUsers(_users);
        setLoading(false);
      },
      (_error) => {
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    handleGetUsers();
    handleGetUserInvites();
  }, [handleGetUsers, handleGetUserInvites]);

  return (
    <Paper className={classes.content} elevation={0} square>
      <Grid
        container
        className={classes.title}
        spacing={3}
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Grid size={12}>
          <Typography variant="h4">{t("Käyttäjät")}</Typography>
        </Grid>

        <Grid size={12}>
          {myUser?.status == 100 ? (
            <Link className={classes.buttonLink} to={"/dashboard/users/invite"}>
              <ButtonPrimary>{t("Kutsu admin käyttäjä")}</ButtonPrimary>
            </Link>
          ) : null}
        </Grid>
      </Grid>

      <Box sx={{ mb: 4 }}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeaderCell} align="left">
                {t("Nimi")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} align="left">
                {t("Käyttäjätunnus")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} align="left">
                {t("Sähköposti")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} align="left">
                {t("Puhelin")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} align="right">
                &nbsp;
              </TableCell>
            </TableRow>
          </TableHead>

          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={5}>
                  <SpinnerCentered />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id != null ? `user-${row.id}` : `user-${row.email}`}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="subtitle2">
                      {row.first_name} {row.last_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.username}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.phone}</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h4">{t("Käyttäjäkutsut")}</Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeaderCell} align="left">
                {t("Sähköposti")}
              </TableCell>
            </TableRow>
          </TableHead>

          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={5}>
                  <SpinnerCentered />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {invitedUsers.map((row) => (
                <TableRow
                  key={
                    row.id != null ? `invite-${row.id}` : `invite-${row.email}`
                  }
                >
                  <TableCell>{row.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </Box>
    </Paper>
  );
};

export default UsersList;
